// ============================================
// TRAVEL SAFETY DATA
// Destination restrictions, citizenship-based warnings, and safety advisories
// ============================================

// Destinations that should NOT be suggested due to extreme danger or impossibility of travel
const RESTRICTED_DESTINATIONS = {
  // Complete no-go zones (OFAC-sanctioned or extreme danger)
  FORBIDDEN: [
    'North Korea', 'DPRK',
    'Syria', 'Damascus',
    'Yemen', 'Sanaa',
    'Libya', 'Tripoli (Libya)',
    'South Sudan', 'Juba',
    'Central African Republic', 'Bangui',
    'Somalia', 'Mogadishu',
    'Afghanistan', 'Kabul',
    'Iran', 'Tehran',
  ],

  // High risk - show strong warning
  HIGH_RISK: [
    'Venezuela', 'Caracas',
    'Haiti', 'Port-au-Prince',
    'Myanmar', 'Yangon',
    'Sudan', 'Khartoum',
    'Mali', 'Bamako',
    'Burkina Faso', 'Ouagadougou',
    'Niger', 'Niamey',
    'Chad', 'N\'Djamena',
    'Iraq', 'Baghdad',
    'Pakistan', 'Karachi', 'Islamabad',
  ],

  // Elevated risk - show advisory
  ELEVATED_RISK: [
    'Russia', 'Moscow', 'St. Petersburg',
    'Belarus', 'Minsk',
    'Cuba', 'Havana',
  ],
};

// Citizenship-based travel restrictions
// Key: Passport country, Value: Countries they cannot or should not visit
const CITIZENSHIP_RESTRICTIONS = {
  'Israel': {
    banned: [
      // Countries that do not recognize Israeli passports
      'Iran', 'Iraq', 'Syria', 'Lebanon', 'Saudi Arabia', 'Yemen', 'Libya',
      'Sudan', 'Algeria', 'Bangladesh', 'Brunei', 'Kuwait', 'Pakistan',
      'Indonesia', 'Malaysia',
      // Note: UAE/Dubai removed - Abraham Accords (2020) opened travel
      // Note: Bahrain removed - Abraham Accords (2020) opened travel
      // Note: Morocco removed - Abraham Accords (2020) opened travel
    ],
    warning: [
      'Jordan', // Peace treaty exists but tensions - can visit
      'Egypt', // Peace treaty exists but may face issues at border
      'Turkey', // Relations strained, extra scrutiny possible
      'Oman', // Officially no relations but generally accepts Israeli tourists
      'Qatar', // Complex relationship, check current status
    ],
    message: 'Israeli passport holders may face entry restrictions in several countries. Always verify current entry requirements before booking.',
  },
  'USA': {
    banned: ['North Korea'], // US passports invalid for travel to DPRK
    warning: [
      'Russia', 'Belarus', // Active travel advisories, detention risk
      'Iran', // No US embassy, hostile relations
      'Cuba', // Travel restrictions, limited categories
      'Venezuela', // No US embassy, crime, arbitrary detention
      'China', // Tensions, exit bans possible
      'Syria', 'Afghanistan', 'Yemen', 'Somalia', 'Libya', 'Sudan', // Level 4 Do Not Travel
    ],
    message: 'US citizens should check current State Department travel advisories.',
  },
  'UK': {
    banned: ['North Korea'],
    warning: [
      'Russia', 'Belarus', // Active FCDO warnings
      'Iran', // No UK embassy since 2011
      'Afghanistan', 'Syria', 'Yemen', 'Somalia', 'Libya', // FCDO advises against all travel
    ],
    message: 'UK citizens should verify FCDO travel advisories.',
  },
  'Canada': {
    banned: ['North Korea'],
    warning: [
      'Russia', 'Belarus', 'Iran', 'Cuba',
      'Afghanistan', 'Syria', 'Yemen', 'Somalia', 'Libya',
      'China', // Arbitrary detention risk
    ],
    message: 'Canadian citizens should check Global Affairs Canada travel advisories.',
  },
  'Australia': {
    banned: ['North Korea'],
    warning: [
      'Russia', 'Belarus', 'Iran',
      'Afghanistan', 'Syria', 'Yemen', 'Somalia', 'Libya',
      'China', // Arbitrary detention risk
    ],
    message: 'Australian citizens should check Smartraveller advisories.',
  },
  'France': {
    banned: ['North Korea'],
    warning: [
      'Russia', 'Belarus', 'Iran',
      'Afghanistan', 'Syria', 'Yemen', 'Somalia', 'Libya', 'Mali', 'Burkina Faso', 'Niger',
      // Sahel region - French citizens targeted
    ],
    message: 'French citizens should check Ministry of Foreign Affairs advisories.',
  },
  'Germany': {
    banned: ['North Korea'],
    warning: [
      'Russia', 'Belarus', 'Iran',
      'Afghanistan', 'Syria', 'Yemen', 'Somalia', 'Libya',
    ],
    message: 'German citizens should check Auswärtiges Amt travel advisories.',
  },
  'Iran': {
    banned: ['Israel'], // Iranian passports stamped "not valid for travel to occupied Palestine"
    warning: [
      'USA', // Extreme vetting, visa difficulties
      'UK', 'Canada', // Similar issues
      'Saudi Arabia', // Regional tensions
    ],
    message: 'Iranian passport holders may face significant visa difficulties and additional scrutiny.',
  },
  'Russia': {
    banned: [],
    warning: [
      'USA', 'UK', 'Canada', 'Australia', // Visa bans, flight restrictions
      'EU', 'France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Poland', // Schengen restrictions
      'Japan', 'South Korea', // Aligned with Western sanctions
    ],
    message: 'Russian citizens face severe travel restrictions to most Western countries due to sanctions.',
  },
  'Belarus': {
    banned: [],
    warning: [
      'USA', 'UK', 'Canada', 'Australia',
      'EU', 'France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Poland',
    ],
    message: 'Belarusian citizens may face visa restrictions in many Western countries.',
  },
  'China': {
    banned: [],
    warning: [
      'Taiwan', // Political sensitivity - use "Chinese Taipei" terminology issues
    ],
    message: 'Chinese passport holders should verify visa requirements. Dual nationality not recognized by PRC.',
  },
  'Taiwan': {
    banned: [],
    warning: [
      'China', // PRC considers Taiwan citizens as Chinese nationals
    ],
    message: 'Taiwanese citizens traveling to mainland China should be aware of political sensitivities.',
  },
  'Pakistan': {
    banned: ['Israel'], // Pakistani passports stamped "valid for all countries except Israel"
    warning: [
      'India', // Tensions, visa difficulties
      'USA', 'UK', // Enhanced screening
    ],
    message: 'Pakistani passport holders may face additional visa requirements and screening.',
  },
  'India': {
    banned: [],
    warning: [
      'Pakistan', // Visa on arrival not available, tensions
      'China', // Border disputes
    ],
    message: 'Indian citizens should check visa requirements, especially for Pakistan.',
  },
  'Bangladesh': {
    banned: ['Israel'],
    warning: [],
    message: 'Bangladeshi passports are not valid for travel to Israel.',
  },
  'Syria': {
    banned: [],
    warning: [
      'USA', 'UK', 'EU', 'Canada', 'Australia', // Severe visa restrictions
      'Gulf States', // Complex relations
    ],
    message: 'Syrian passport holders face significant visa difficulties in most Western countries.',
  },
};

// Walking safety by destination type/area
const WALKING_SAFETY = {
  // Areas where walking should be avoided/limited
  AVOID_WALKING: {
    // Time-based restrictions
    afterDark: [
      'isolated areas', 'parks after sunset', 'beaches at night',
      'industrial zones', 'poorly lit streets',
    ],
    // Location-based restrictions for certain destinations
    destinations: {
      'Johannesburg': 'Walking is generally not recommended except in secure areas like Sandton. Use Uber/taxis.',
      'Rio de Janeiro': 'Avoid walking in favelas or isolated beach areas after dark. Stick to well-traveled areas.',
      'Cape Town': 'Central city safe during day but avoid walking at night. Do not flash valuables.',
      'Manila': 'Many areas safe but avoid walking in Tondo and some Quezon City areas.',
      'Nairobi': 'Use taxis for most trips. Walking only in very busy commercial areas during day.',
      'Cartagena': 'Old Town is walkable but avoid Getsemani at night. Use taxis to other areas.',
      'Mexico City': 'Centro, Roma, Condesa safe for walking. Avoid Tepito and some outlying areas.',
      'Lima': 'Miraflores and Barranco walkable. Use caution in Centro and avoid Callao.',
      'Bogota': 'Zona Rosa and Usaquen safe. Avoid walking in La Candelaria after dark.',
      'Cairo': 'Generally safe but harassment common. Women should dress conservatively.',
      'Mumbai': 'Mostly safe but crowded. Avoid Dharavi unless on organized tour.',
      'Delhi': 'Tourist areas safe but scams common. Women should take extra precautions.',
      'Marrakech': 'Medina safe but confusing. Hire a guide. Be wary of aggressive sellers.',
    },
  },

  // General walking safety tips
  TIPS: [
    'Stay in well-lit, populated areas after dark',
    'Keep valuables concealed and secure',
    'Use hotel-recommended taxi services or ride apps',
    'Avoid displaying expensive jewelry or electronics',
    'Trust your instincts - if an area feels unsafe, leave',
    'Let someone know your itinerary when exploring',
  ],
};

// LGBTQ+ safety ratings
const LGBTQ_SAFETY = {
  // Countries where LGBTQ+ travelers face legal or safety risks
  HIGH_RISK: [
    'Saudi Arabia', 'Iran', 'Yemen', 'Sudan', 'Nigeria (north)',
    'Mauritania', 'Afghanistan', 'Pakistan', 'Qatar', 'UAE',
    'Brunei', 'Somalia', 'Uganda', 'Tanzania', 'Kenya',
    'Jamaica', 'Barbados', 'Trinidad and Tobago',
    'Russia', 'Chechnya', 'Egypt', 'Morocco', 'Tunisia',
  ],
  MODERATE_RISK: [
    'Singapore', 'Malaysia', 'Indonesia', 'India',
    'China', 'Turkey', 'Poland', 'Hungary',
  ],
  ADVICE: 'LGBTQ+ travelers should research local laws and social attitudes. Public displays of affection may attract unwanted attention or legal consequences in some countries.',
};

// Solo female traveler safety
const SOLO_FEMALE_SAFETY = {
  // Countries requiring extra caution for solo female travelers
  HIGH_CAUTION: [
    'India', 'Egypt', 'Morocco', 'Turkey', 'Jordan',
    'Pakistan', 'Bangladesh', 'Indonesia', 'Malaysia',
  ],
  MODERATE_CAUTION: [
    'Mexico', 'Brazil', 'Colombia', 'Peru',
    'South Africa', 'Kenya', 'Tanzania',
    'Vietnam', 'Cambodia', 'Philippines',
  ],
  ADVICE: 'Solo female travelers should dress modestly in conservative countries, avoid walking alone at night, use reputable transportation, and stay in well-reviewed accommodations.',
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Parse passport_country from its stored format (JSON string array, plain string, or array)
 * into a flat array of country strings.
 *
 * Handles:
 *   '["Israel"]'                → ['Israel']
 *   '["United States", "Israel"]' → ['United States', 'Israel']
 *   'Israel'                    → ['Israel']
 *   ['Israel']                  → ['Israel']
 *   null / undefined / ''       → []
 */
export const parsePassportCountries = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
      return [raw]; // JSON-parsed to a non-array (e.g. a number) — treat as plain string
    } catch {
      return [raw]; // Already a plain string like "Israel"
    }
  }
  return [];
};

/**
 * Check if a destination is restricted
 */
export const checkDestinationRestriction = (destination) => {
  // Use word boundary matching to avoid false positives
  const matchesPlace = (place) => {
    const regex = new RegExp(`\\b${place}\\b`, 'i');
    return regex.test(destination);
  };

  // Check forbidden destinations
  if (RESTRICTED_DESTINATIONS.FORBIDDEN.some(matchesPlace)) {
    return {
      allowed: false,
      level: 'forbidden',
      message: `${destination} is not a recommended travel destination due to extreme safety concerns, ongoing conflict, or travel restrictions.`,
    };
  }

  // Check high risk
  if (RESTRICTED_DESTINATIONS.HIGH_RISK.some(matchesPlace)) {
    return {
      allowed: true,
      level: 'high_risk',
      message: `⚠️ ${destination} has significant safety concerns. Check your government's travel advisory before booking.`,
    };
  }

  // Check elevated risk
  if (RESTRICTED_DESTINATIONS.ELEVATED_RISK.some(matchesPlace)) {
    return {
      allowed: true,
      level: 'elevated_risk',
      message: `⚠️ ${destination} has elevated travel advisories. Verify current conditions and visa requirements.`,
    };
  }

  return { allowed: true, level: 'normal', message: null };
};

/**
 * Check citizenship-based restrictions
 */
export const checkCitizenshipRestriction = (passportCountry, destination) => {
  if (!passportCountry) return { allowed: true, message: null };

  const restrictions = CITIZENSHIP_RESTRICTIONS[passportCountry];
  if (!restrictions) return { allowed: true, message: null };

  // Use word boundary matching to avoid false positives like "UK" matching "Phuket"
  const matchesCountry = (country) => {
    const regex = new RegExp(`\\b${country}\\b`, 'i');
    return regex.test(destination);
  };

  // Check banned
  if (restrictions.banned?.some(matchesCountry)) {
    return {
      allowed: false,
      level: 'banned',
      message: `${passportCountry} passport holders cannot travel to ${destination}. Entry will be denied.`,
    };
  }

  // Check warning
  if (restrictions.warning?.some(matchesCountry)) {
    return {
      allowed: true,
      level: 'warning',
      message: `${passportCountry} passport holders should exercise caution when traveling to ${destination}. ${restrictions.message}`,
    };
  }

  return { allowed: true, level: 'normal', message: null };
};

/**
 * Get walking safety info for a destination
 */
const getWalkingSafetyInfo = (destination) => {
  const destLower = destination.toLowerCase();

  for (const [city, warning] of Object.entries(WALKING_SAFETY.AVOID_WALKING.destinations)) {
    if (destLower.includes(city.toLowerCase())) {
      return {
        hasWarning: true,
        warning,
        tips: WALKING_SAFETY.TIPS,
      };
    }
  }

  return {
    hasWarning: false,
    warning: null,
    tips: WALKING_SAFETY.TIPS,
  };
};

/**
 * Get LGBTQ+ safety info
 */
const getLGBTQSafetyInfo = (destination) => {
  const destLower = destination.toLowerCase();

  if (LGBTQ_SAFETY.HIGH_RISK.some(d => destLower.includes(d.toLowerCase()))) {
    return {
      level: 'high_risk',
      message: `⚠️ LGBTQ+ travelers face significant legal and safety risks in ${destination}. Same-sex relationships may be criminalized.`,
      advice: LGBTQ_SAFETY.ADVICE,
    };
  }

  if (LGBTQ_SAFETY.MODERATE_RISK.some(d => destLower.includes(d.toLowerCase()))) {
    return {
      level: 'moderate_risk',
      message: `LGBTQ+ travelers should exercise discretion in ${destination}. Public displays of affection may attract negative attention.`,
      advice: LGBTQ_SAFETY.ADVICE,
    };
  }

  return { level: 'safe', message: null, advice: null };
};

/**
 * Get solo female safety info
 */
const getSoloFemaleSafetyInfo = (destination) => {
  const destLower = destination.toLowerCase();

  if (SOLO_FEMALE_SAFETY.HIGH_CAUTION.some(d => destLower.includes(d.toLowerCase()))) {
    return {
      level: 'high_caution',
      message: `Solo female travelers should exercise extra caution in ${destination}. Dress modestly and avoid isolated areas.`,
      advice: SOLO_FEMALE_SAFETY.ADVICE,
    };
  }

  if (SOLO_FEMALE_SAFETY.MODERATE_CAUTION.some(d => destLower.includes(d.toLowerCase()))) {
    return {
      level: 'moderate_caution',
      message: `Solo female travelers should take standard precautions in ${destination}.`,
      advice: SOLO_FEMALE_SAFETY.ADVICE,
    };
  }

  return { level: 'safe', message: null, advice: null };
};

/**
 * Get comprehensive safety report for a destination
 * @param {string} destination
 * @param {string|string[]|null} passportCountryOrCountries — raw passport_country value (JSON string, plain string, or array)
 */
export const getDestinationSafetyReport = (destination, passportCountryOrCountries = null) => {
  const report = {
    destination,
    overallSafe: true,
    warnings: [],
    restrictions: [],
  };

  // Check destination restriction
  const destCheck = checkDestinationRestriction(destination);
  if (!destCheck.allowed) {
    report.overallSafe = false;
    report.restrictions.push(destCheck.message);
  } else if (destCheck.message) {
    report.warnings.push(destCheck.message);
  }

  // Check citizenship restriction for each passport country
  const passportCountries = parsePassportCountries(passportCountryOrCountries);
  for (const country of passportCountries) {
    const citizenCheck = checkCitizenshipRestriction(country, destination);
    if (!citizenCheck.allowed) {
      report.overallSafe = false;
      report.restrictions.push(citizenCheck.message);
    } else if (citizenCheck.message) {
      report.warnings.push(citizenCheck.message);
    }
  }

  // Walking safety
  const walkingSafety = getWalkingSafetyInfo(destination);
  if (walkingSafety.hasWarning) {
    report.walkingSafety = walkingSafety;
  }

  // LGBTQ+ safety
  const lgbtqSafety = getLGBTQSafetyInfo(destination);
  if (lgbtqSafety.level !== 'safe') {
    report.lgbtqSafety = lgbtqSafety;
    if (lgbtqSafety.level === 'high_risk') {
      report.warnings.push(lgbtqSafety.message);
    }
  }

  // Solo female safety
  const soloFemaleSafety = getSoloFemaleSafetyInfo(destination);
  if (soloFemaleSafety.level !== 'safe') {
    report.soloFemaleSafety = soloFemaleSafety;
  }

  return report;
};

// No default export — use named exports only
